import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, ButtonToolbar, Dropdown } from 'react-bootstrap'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { ActiveFilterViewer, Filters } from '@rankmi/rankmi-table'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/externalCourses.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import { ExternalCourseModelAPI } from 'models/externalCourse.model'
import { ExternalCoursesListParams } from 'api/administration/externalCourses/GetList'
import { generateFilterViewerLocale } from 'helpers/translations.helper'
import Table from './components/table'
import UploadButton from './components/upload-button/UploadButton'
import { useFiltersColumns } from './hooks/filtersColumns'
import { useTranslationExternalExternalCourses } from './hooks'
import { DeleteMany } from './components/DeleteMany'
import styles from './externalCourses.module.scss'
import { useHistory } from 'react-router'
import { PathRoutes } from 'enum/routes'

const Index: React.FC = () => {
  let bounceId: any
  const dispatch = useDispatch()
  const { total } = useSelector(
    ({ externalCourses }: IMainState) => externalCourses
  )
  const history = useHistory()
  const { permission } = useSelector(({ user }: IMainState) => user)
  const [searchText, setSearchText] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({} as any)
  const [page, setPage] = useState(1)
  const [deleteMany, setDeleteMany] = useState(false)
  const [sort, setSort] = useState({})
  const [perPage, setPerPage] = useState(50)
  const [selectedRows, setSelectedRows] = useState<
    ExternalCourseModelAPI['id'][]
  >([])
  const [fastFilters, setFastFilters] = useState(0 as any)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  const tRelative = useTranslationExternalExternalCourses()
  const searchTextRef = useRef<any>()
  const filtersRef = useRef()
  const filtersColumns = useFiltersColumns()
  const [translations, setTranslations] = useState<any>()
  useEffect(() => {
    setTranslations(() => generateFilterViewerLocale())
  }, [])
  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  useEffect(() => {
    searchTextRef.current = searchText
    filtersRef.current = selectedFilters
    const timer = setTimeout(() => {
      dispatch(
        fromActions.Actions.getList({
          page,
          searchText,
          sort,
          usersFilter: selectedFilters,
          fastFilters,
          perPage,
        })
      )
    }, 1000)
    return () => clearTimeout(timer)
  }, [page, sort, searchText, perPage, selectedFilters, fastFilters, dispatch])

  useEffect(() => {
    const { permissions } = permission
    if (
      permissions &&
      !permission.loading &&
      !permissions.canDownload &&
      !permissions.canEdit &&
      !permissions.canMonitor
    )
      history.push(PathRoutes.MAIN)
  }, [history, permission])

  const handleSearch = (value: string) => {
    if (bounceId) {
      clearTimeout(bounceId)
    }
    bounceId = setTimeout(() => {
      setSearchText(value.trim())
    }, 1000)
  }

  const getParams: ExternalCoursesListParams = {
    page,
    searchText,
    sort,
    usersFilter: selectedFilters,
    fastFilters,
    perPage,
  }

  const destroyExternalCourses = () => {
    dispatch(
      fromActions.Actions.destroyExternalCourses({
        ids: selectedRows,
        params: getParams,
        allSelected,
        callback: () => {
          setSelectedRows([])
        },
      })
    )
    setDeleteMany(false)
  }

  const handleDelete = (id: number) => {
    dispatch(
      fromActions.Actions.destroyExternalCourse({
        id,
        params: getParams,
      })
    )
  }

  const handleDownload = (id: number) => {
    dispatch(
      fromActions.Actions.downloadExternalCourses({
        id,
      })
    )
  }

  const selectedCount = !allSelected
    ? selectedRows.length
    : total - selectedRows.length

  return (
    <>
      <Row className={'d-flex align-items-center mb-3'}>
        <Col>
          <h1>{tRelative('list.managementTitle')}</h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <ButtonToolbar className="float-left">
            <Filters
              columns={filtersColumns}
              onChange={(data: any) => {
                return setSelectedFilters(data)
              }}
              open={showFiltersModal}
              onVisibilityChange={(state: boolean) =>
                setShowFiltersModal(state)
              }
              locale={translations}
            />
          </ButtonToolbar>
          <ButtonToolbar className={`float-right ${styles.buttons}`}>
            {(permission.admin || permission.superadmin) && (
              <Dropdown
                id="dropdown-manage"
                className={styles.dropdownManage}
                alignRight
              >
                <Dropdown.Toggle
                  id="dropdown-manage-toggle"
                  bsPrefix="noCaret"
                  variant={'secondary'}
                >
                  {tRelative('list.dropdown.actions')}{' '}
                  <FontAwesomeIcon icon={faChevronDown} />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <UploadButton />
                  <Dropdown.Item
                    disabled={!allSelected && !selectedRows.length}
                    onClick={() => setDeleteMany(true)}
                  >
                    {tRelative('list.dropdown.removeCourses', {
                      count: selectedCount,
                    })}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          <ActiveFilterViewer
            activeFilters={selectedFilters}
            onChange={(data: any) => setSelectedFilters(data)}
            openFilters={(state: boolean) => setShowFiltersModal(state)}
            columns={filtersColumns}
            locale={translations}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Table
            onDelete={handleDelete}
            setSearch={handleSearch}
            setPage={setPage}
            setSort={setSort}
            setPerPage={setPerPage}
            setSelectedRows={setSelectedRows}
            selectedRows={selectedRows}
            setFastFilters={setFastFilters}
            setAllSelected={setAllSelected}
            onDownload={handleDownload}
          />
        </Col>
      </Row>
      <DeleteMany
        show={deleteMany}
        count={selectedCount}
        onClose={() => {
          setDeleteMany(false)
        }}
        onAccept={() => {
          destroyExternalCourses()
        }}
      />
    </>
  )
}

export default Index
